// =====================================================
// DEMO DATA - Genre-neutral sample data for new users
// =====================================================

/**
 * Generates demo data for new users to explore
 * All content is intentionally generic to work for:
 * - Bands, DJs, solo artists
 * - Wedding/event performers
 * - Hobbyists, cover bands, originals
 */

// Helper to get dates relative to today
const getDate = (daysFromNow) => {
  const date = new Date();
  date.setDate(date.getDate() + daysFromNow);
  return date.toISOString().split('T')[0];
};

// Helper to generate IDs
const generateId = () => Date.now().toString() + Math.random().toString(36).substr(2, 9);

export const generateDemoData = () => {
  const now = new Date().toISOString();
  
  // =====================================================
  // DEMO SHOWS
  // =====================================================
  const demoShows = {
    // Past show 1 - 3 weeks ago (completed with full data)
    [getDate(-21)]: [
      {
        id: 'demo-show-1',
        venue: 'The Blue Room',
        city: 'Austin',
        state: 'TX',
        date: getDate(-21),
        loadIn: '5:00 PM',
        soundcheck: '6:00 PM',
        showtime: '8:00 PM',
        guarantee: 800,
        actualPayout: 950,
        status: 'completed',
        attendance: 127,
        eventType: 'show',
        notes: 'Great crowd! Sold out of medium shirts.',
        contactName: 'Jamie Rivers',
        contactEmail: 'jamie@theblueroom.com',
        contactPhone: '(512) 555-0123',
        expenses: [
          { id: 'exp-1', description: 'Gas', amount: 45, category: 'travel' },
          { id: 'exp-2', description: 'Dinner', amount: 62, category: 'meals' },
        ],
        merchSales: 285,
        createdAt: now,
      }
    ],
    
    // Past show 2 - 10 days ago (completed, simpler)
    [getDate(-10)]: [
      {
        id: 'demo-show-2',
        venue: 'Riverside Events Center',
        city: 'Dallas',
        state: 'TX',
        date: getDate(-10),
        loadIn: '3:00 PM',
        showtime: '7:00 PM',
        guarantee: 1200,
        actualPayout: 1200,
        status: 'completed',
        attendance: 85,
        eventType: 'show',
        notes: 'Corporate event - they want us back for their holiday party.',
        contactName: 'Morgan Chen',
        contactEmail: 'mchen@riversideevents.com',
        expenses: [
          { id: 'exp-3', description: 'Parking', amount: 20, category: 'travel' },
        ],
        merchSales: 0,
        createdAt: now,
      }
    ],
    
    // Upcoming show 1 - 5 days from now (confirmed)
    [getDate(5)]: [
      {
        id: 'demo-show-3',
        venue: 'The Grand Hall',
        city: 'Houston',
        state: 'TX',
        date: getDate(5),
        loadIn: '4:00 PM',
        soundcheck: '5:30 PM',
        showtime: '9:00 PM',
        guarantee: 1000,
        status: 'confirmed',
        eventType: 'show',
        notes: 'Opening for another act. 45 min set.',
        contactName: 'Alex Thompson',
        contactEmail: 'booking@grandhall.com',
        contactPhone: '(713) 555-0456',
        createdAt: now,
      }
    ],
    
    // Upcoming show 2 - 2 weeks from now (tentative - good for poll demo)
    [getDate(14)]: [
      {
        id: 'demo-show-4',
        venue: 'Private Event',
        city: 'San Antonio',
        state: 'TX',
        date: getDate(14),
        showtime: '6:00 PM',
        guarantee: 1500,
        status: 'tentative',
        eventType: 'show',
        notes: 'Wedding reception - 3 hour set with breaks. Need to confirm availability.',
        contactName: 'Sam & Jordan',
        contactEmail: 'samjordan2024@email.com',
        createdAt: now,
      }
    ],

    // Practice session - 2 days from now
    [getDate(2)]: [
      {
        id: 'demo-event-1',
        venue: 'Practice Space',
        city: 'Local',
        date: getDate(2),
        showtime: '7:00 PM',
        status: 'confirmed',
        eventType: 'other',
        notes: 'Run through setlist for Houston show',
        createdAt: now,
      }
    ],
  };

  // =====================================================
  // DEMO INVENTORY
  // =====================================================
  const demoInventory = [
    {
      id: 'demo-merch-1',
      name: 'Logo T-Shirt',
      category: 'Apparel',
      price: 25,
      cost: 8,
      variants: [
        { size: 'S', quantity: 12 },
        { size: 'M', quantity: 5 },  // Low stock!
        { size: 'L', quantity: 18 },
        { size: 'XL', quantity: 15 },
        { size: 'XXL', quantity: 8 },
      ],
      lowStockThreshold: 10,
      createdAt: now,
    },
    {
      id: 'demo-merch-2',
      name: 'Embroidered Hat',
      category: 'Apparel',
      price: 30,
      cost: 12,
      variants: [
        { size: 'One Size', quantity: 24 },
      ],
      lowStockThreshold: 10,
      createdAt: now,
    },
    {
      id: 'demo-merch-3',
      name: 'Sticker Pack',
      category: 'Accessories',
      price: 5,
      cost: 0.75,
      variants: [
        { size: 'Pack of 5', quantity: 150 },
      ],
      lowStockThreshold: 30,
      createdAt: now,
    },
    {
      id: 'demo-merch-4',
      name: 'Signed Poster',
      category: 'Prints',
      price: 15,
      cost: 3,
      variants: [
        { size: '11x17', quantity: 42 },
      ],
      lowStockThreshold: 15,
      createdAt: now,
    },
    {
      id: 'demo-merch-5',
      name: 'Album / EP',
      category: 'Music',
      price: 12,
      cost: 4,
      variants: [
        { size: 'CD', quantity: 35 },
        { size: 'Digital Download Card', quantity: 50 },
      ],
      lowStockThreshold: 15,
      createdAt: now,
    },
  ];

  // =====================================================
  // DEMO CONTACTS
  // =====================================================
  const demoContacts = [
    {
      id: 'demo-contact-1',
      name: 'Jamie Rivers',
      role: 'Venue Booker',
      venue: 'The Blue Room',
      email: 'jamie@theblueroom.com',
      phone: '(512) 555-0123',
      city: 'Austin',
      state: 'TX',
      notes: 'Books Thursdays and Saturdays. Prefers email.',
      createdAt: now,
    },
    {
      id: 'demo-contact-2',
      name: 'Morgan Chen',
      role: 'Event Coordinator',
      venue: 'Riverside Events Center',
      email: 'mchen@riversideevents.com',
      phone: '(214) 555-0789',
      city: 'Dallas',
      state: 'TX',
      notes: 'Corporate events mostly. Good pay, professional setup.',
      createdAt: now,
    },
    {
      id: 'demo-contact-3',
      name: 'Alex Thompson',
      role: 'Talent Buyer',
      venue: 'The Grand Hall',
      email: 'booking@grandhall.com',
      phone: '(713) 555-0456',
      city: 'Houston',
      state: 'TX',
      notes: 'Books 2-3 months in advance. Likes electronic press kits.',
      createdAt: now,
    },
    {
      id: 'demo-contact-4',
      name: 'Chris Audio',
      role: 'Sound Engineer',
      venue: 'Freelance',
      email: 'chris@soundpro.com',
      phone: '(512) 555-0999',
      city: 'Austin',
      state: 'TX',
      notes: 'Great live sound. $200/show. Book early - busy weekends.',
      createdAt: now,
    },
  ];

  // =====================================================
  // DEMO POLLS
  // =====================================================
  const demoPolls = [
    {
      id: 'demo-poll-1',
      question: 'Should we take this gig?',
      description: 'Private event in San Antonio - $1,500 guarantee, 3 hour set. It\'s a wedding reception.',
      options: [
        { id: 'yes', text: "Yes, I'm in! ✅", votes: ['demo-member-2'] },
        { id: 'no', text: "No, can't do it ❌", votes: [] },
        { id: 'maybe', text: "Maybe, need more info 🤔", votes: ['demo-member-3'] },
      ],
      status: 'active',
      createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
      createdBy: 'demo-member-1',
      showInfo: {
        venue: 'Private Event',
        city: 'San Antonio',
        date: getDate(14),
        guarantee: '1500',
      },
    },
    {
      id: 'demo-poll-2',
      question: 'New merch design - which one?',
      description: 'Picking the design for our next t-shirt order.',
      options: [
        { id: 'opt-1', text: 'Design A - Minimalist logo', votes: ['demo-member-1', 'demo-member-3'] },
        { id: 'opt-2', text: 'Design B - Vintage style', votes: ['demo-member-2'] },
        { id: 'opt-3', text: 'Design C - Bold & colorful', votes: [] },
      ],
      status: 'active',
      createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days ago
      createdBy: 'demo-member-2',
    },
  ];

  // =====================================================
  // DEMO GENERAL EXPENSES
  // =====================================================
  const demoGeneralExpenses = [
    {
      id: 'demo-expense-1',
      description: 'Strings & Picks',
      amount: 45,
      category: 'equipment',
      date: getDate(-15),
      createdAt: now,
    },
    {
      id: 'demo-expense-2',
      description: 'Social Media Ads',
      amount: 50,
      category: 'marketing',
      date: getDate(-8),
      createdAt: now,
    },
    {
      id: 'demo-expense-3',
      description: 'Website Hosting (Annual)',
      amount: 120,
      category: 'marketing',
      date: getDate(-30),
      createdAt: now,
    },
  ];

  // =====================================================
  // DEMO SALES LOG (for merch tracking)
  // =====================================================
  const demoSalesLog = [
    {
      id: 'demo-sale-1',
      showId: 'demo-show-1',
      showName: 'The Blue Room - Austin',
      date: getDate(-21),
      items: [
        { itemId: 'demo-merch-1', name: 'Logo T-Shirt', size: 'M', quantity: 3, price: 25 },
        { itemId: 'demo-merch-1', name: 'Logo T-Shirt', size: 'L', quantity: 2, price: 25 },
        { itemId: 'demo-merch-3', name: 'Sticker Pack', size: 'Pack of 5', quantity: 8, price: 5 },
        { itemId: 'demo-merch-4', name: 'Signed Poster', size: '11x17', quantity: 5, price: 15 },
      ],
      total: 240,
      createdAt: now,
    },
  ];

  return {
    shows: demoShows,
    inventory: demoInventory,
    contacts: demoContacts,
    polls: demoPolls,
    generalExpenses: demoGeneralExpenses,
    salesLog: demoSalesLog,
    isDemoData: true,
  };
};

// =====================================================
// DEMO BAND INFO
// =====================================================
export const demoBandInfo = {
  id: 'demo-band',
  name: 'Demo Band',
  createdAt: new Date().toISOString(),
  isDemo: true,
  members: [
    { id: 'demo-member-1', name: 'You', role: 'Admin', isCurrentUser: true },
    { id: 'demo-member-2', name: 'Alex', role: 'Member' },
    { id: 'demo-member-3', name: 'Sam', role: 'Member' },
  ],
};

// =====================================================
// STATS CALCULATOR (for demo dashboard)
// =====================================================
export const calculateDemoStats = (data) => {
  const { shows, inventory, generalExpenses } = data;
  
  // Flatten shows
  const allShows = Object.values(shows).flat().filter(s => s.eventType !== 'other');
  const completedShows = allShows.filter(s => s.status === 'completed');
  const upcomingShows = allShows.filter(s => s.status === 'confirmed' || s.status === 'tentative');
  
  // Revenue from completed shows
  const totalRevenue = completedShows.reduce((sum, show) => {
    const payout = show.actualPayout || show.guarantee || 0;
    const merch = show.merchSales || 0;
    return sum + payout + merch;
  }, 0);
  
  // Expenses
  const showExpenses = completedShows.reduce((sum, show) => {
    return sum + (show.expenses?.reduce((s, e) => s + e.amount, 0) || 0);
  }, 0);
  const generalExpenseTotal = generalExpenses.reduce((sum, e) => sum + e.amount, 0);
  const totalExpenses = showExpenses + generalExpenseTotal;
  
  // Inventory value
  const inventoryValue = inventory.reduce((sum, item) => {
    const totalQty = item.variants.reduce((q, v) => q + v.quantity, 0);
    return sum + (totalQty * item.price);
  }, 0);
  
  // Low stock items
  const lowStockItems = inventory.filter(item => {
    const totalQty = item.variants.reduce((q, v) => q + v.quantity, 0);
    return totalQty <= item.lowStockThreshold;
  });

  return {
    totalShows: allShows.length,
    completedShows: completedShows.length,
    upcomingShows: upcomingShows.length,
    totalRevenue,
    totalExpenses,
    netProfit: totalRevenue - totalExpenses,
    inventoryValue,
    lowStockCount: lowStockItems.length,
    avgPerShow: completedShows.length > 0 ? Math.round(totalRevenue / completedShows.length) : 0,
  };
};

export default generateDemoData;